interface DataConfig {
  key: string;
  graph: string;
  transform: (data: any, fullData?: any) => any;
}

export const previewAggregateSentimentAnalyticsDataConfigs: DataConfig[] = [
  {
    key: 'org_health_score',
    graph: 'ScoreCard',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'org_health_score');
      const keyData = metric?.data;
      const normalizedScore = Number(keyData?.score ?? keyData?.value ?? keyData?.score_value ?? 0);
      const normalizedMax = Number(keyData?.max_score ?? 100);
      const normalizedTrend = Number(keyData?.trend_percentage ?? keyData?.trend ?? 0);
      const healthData = {
        score: normalizedScore,
        maxScore: normalizedMax,
        trendPercentage: normalizedTrend,
        label: keyData?.score_label ?? 'Overall Score',
        subLabel: keyData?.summary_text ?? `Trend: ${normalizedTrend}%`,
        subPoints: [
          `Score: ${normalizedScore} / ${normalizedMax}`,
          `Sentiment: ${keyData?.components?.sentiment_weight || 0}`,
          `Engagement: ${keyData?.components?.engagement_weight || 0}`,
          `Communication Patterns: ${keyData?.components?.communication_weight || 0}`
        ],
        tooltipDescription: `${keyData?.calculation_note || ''} ${keyData?.tooltip_info?.what_it_measures || ''}`,
        points: keyData?.tooltip_info?.suggested_actions || []
      };
      const series = keyData?.trend_sparkline?.map((item: any) => ({ date: item.date, score: item.score })) || [];
      return { payload: series, details: healthData || {} };
    }
  },
  {
    key: 'risk_alerts',
    graph: 'AlertView',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'risk_alerts');
      const keyData = metric?.data;
      const criticalAlerts = keyData?.critical_count || 0;
      const alertData = {
        totalAlerts: keyData?.total_alerts || 0,
        criticalAlerts: criticalAlerts,
        highAlerts: keyData?.high_count || 0,
        mediumAlerts: keyData?.medium_count || 0,
        alertsList: criticalAlerts === 0 ? [] : keyData?.alerts || [],
        points: keyData?.tooltip_info?.suggested_actions || [],
        listLabel: 'Recent Critical Alerts',
        tooltipDescription: `${keyData?.summary_description || ''} ${keyData?.tooltip_info?.what_it_measures || ''}`,
      };
      const payload = {
        title: 'CALLS REQUIRING REVIEW',
        subtitle: '(AI Flagged)',
        value: keyData?.total_alerts?.toString() || '0',
        label: 'Total Alerts',
        breakdown: [
          { icon: '🔴', label: 'Critical', value: keyData?.critical_count || 0 },
          { icon: '🟠', label: 'High', value: keyData?.high_count || 0 },
          { icon: '🟡', label: 'Medium', value: keyData?.medium_count || 0 }
        ],
        link: 'View All Alerts →'
      };
      return { payload, details: alertData || {} };
    }
  },
  {
    key: 'sentiment_trend',
    graph: 'StackedAreaChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'sentiment_trend');
      const sentimentData = metric?.data;
      const currentPositive = sentimentData?.period_summary?.positive || 0;
      const currentNeutral = sentimentData?.period_summary?.neutral || 0;
      const currentNegative = sentimentData?.period_summary?.negative || 0;
      const previousPositive = sentimentData?.previous_period_summary?.positive || 0;
      const previousNeutral = sentimentData?.previous_period_summary?.neutral || 0;
      const previousNegative = sentimentData?.previous_period_summary?.negative || 0;
      const positiveChange = currentPositive - previousPositive;
      const neutralChange = currentNeutral - previousNeutral;
      const negativeChange = currentNegative - previousNegative;
      
      const formatChange = (value: number, sentimentType: 'positive' | 'negative') => {
        const absValue = Math.abs(value);
        const direction = value > 0 ? 'UP' : value < 0 ? 'DOWN' : 'stable';
        const symbol = value > 0 ? '+' : '';
        if (value === 0) return null;
        const isImprovement = (sentimentType === 'positive' && value > 0) || (sentimentType === 'negative' && value < 0);
        const status = isImprovement ? '(improving)' : '';
        return `${sentimentType.charAt(0).toUpperCase() + sentimentType.slice(1)} sentiment ${direction} ${symbol}${absValue.toFixed(1)}% vs previous period ${status}`;
      };
      const subPoints = [];
      if (Math.abs(positiveChange) >= 0.1) {
        subPoints.push(formatChange(positiveChange, 'positive'));
      }
      
      if (Math.abs(negativeChange) >= 0.1) {
        subPoints.push(formatChange(negativeChange, 'negative'));
      }
      if (subPoints.length === 0) {
        subPoints.push('Sentiment levels stable compared to previous period');
        subPoints.push(`Current distribution: ${currentPositive}% positive, ${currentNeutral}% neutral, ${currentNegative}% negative`);
      } else if (subPoints.length === 1) {
        if (Math.abs(neutralChange) >= 0.1) {
          const neutralDirection = neutralChange > 0 ? 'UP' : 'DOWN';
          const neutralSymbol = neutralChange > 0 ? '+' : '';
          subPoints.push(`Neutral sentiment ${neutralDirection} ${neutralSymbol}${Math.abs(neutralChange).toFixed(1)}% vs previous period`);
        } else {
          subPoints.push(`Current distribution: ${currentPositive}% positive, ${currentNeutral}% neutral, ${currentNegative}% negative`);
        }
      }
      const finalSubPoints = subPoints.slice(0, 2);
      const detailsData = {
        bottomDescription: `Positive: ${currentPositive}% | Neutral: ${currentNeutral}% | Negative: ${currentNegative}%`,
        subPoints: finalSubPoints,
        tooltipDescription: `${sentimentData?.summary_description || ''} ${sentimentData?.tooltip_info?.what_it_measures || ''}`,
        points: sentimentData?.tooltip_info?.suggested_actions || []
      };

      let series = [];
      if (sentimentData?.series && sentimentData.series.length > 0) {
        series = sentimentData.series;
      }
      return { payload: series, details: detailsData || {} };
    }
  },
  {
    key: 'call_volume',
    graph: 'BarChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'call_volume');
      const comparisonData = metric?.data;
      const totalCalls = comparisonData?.total_calls || 0;
      const completionRate = comparisonData?.overall_completion_rate || 0;
      const volumeChange = comparisonData?.vs_previous_period?.volume_change_pct || 0;
      const rateChange = comparisonData?.vs_previous_period?.rate_change_pct || 0;
      const avgDailyCalls = comparisonData?.avg_daily_calls || 0;
      const subPoints = [];
      
      if (Math.abs(volumeChange) >= 0.1) {
        const volumeDirection = volumeChange > 0 ? 'UP' : 'DOWN';
        const volumeSymbol = volumeChange > 0 ? '+' : '';
        const volumeStatus = volumeChange > 0 ? '(growing)' : '(declining)';
        subPoints.push(`Call Volume ${volumeDirection} ${volumeSymbol}${Math.abs(volumeChange).toFixed(1)}% vs Previous Period ${volumeStatus}`);
      } else {
        subPoints.push(`Call Volume stable with ${totalCalls} total calls (avg ${avgDailyCalls.toFixed(1)} calls/day)`);
      }
      
      if (Math.abs(rateChange) >= 0.1) {
        const rateDirection = rateChange > 0 ? 'improved' : 'decreased';
        const rateSymbol = rateChange > 0 ? '+' : '';
        subPoints.push(`Completion Rate ${rateDirection} ${rateSymbol}${Math.abs(rateChange).toFixed(1)}pp to ${completionRate.toFixed(1)}%`);
      } else {
        const qualityStatus = completionRate >= 90 ? 'excellent' : completionRate >= 70 ? 'good' : 'needs attention';
        subPoints.push(`Completion Rate ${completionRate.toFixed(1)}% - ${qualityStatus} call quality`);
      }
      
      const detailsData = {
        bottomDescription: `Total Calls: ${totalCalls} | Avg Completion Rate: ${completionRate.toFixed(1)}% | Daily Avg: ${avgDailyCalls.toFixed(1)}`,
        subPoints: subPoints,
        tooltipDescription: `${comparisonData?.summary_description || ''} ${comparisonData?.tooltip_info?.what_it_measures || ''}`,
        points: comparisonData?.tooltip_info?.suggested_actions || []
      };
      const payload = (comparisonData?.trend_series || []).map((item: any) => ({ period: item.date, total_calls: item.total_calls, completed_calls: item.completed_calls }));
      return { payload, details: detailsData || {} };
    }
  },
  {
    key: 'emotion_heatmap',
    graph: 'HeatMap',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'emotion_heatmap');
      const heatmapData = metric?.data;
      const detailsData = {
        bottomDescription: (heatmapData?.legend || []).map((item: any) => `${item.label}`).join(' | '),
        tooltipDescription: `${heatmapData?.summary_description || ''} ${heatmapData?.tooltip_info?.what_it_measures || ''}`,
        points: heatmapData?.tooltip_info?.suggested_actions || [],
        subPoints: [
          'Heatmap shows call volume distribution over time',
          'Darker colors indicate higher call volumes'
        ]
      };
      const heatmapDataUpdated = (heatmapData?.cells || []).map((cell: any) => ({ ...cell, date: cell?.date, value: typeof cell?.call_count === 'number' ? cell.call_count : (cell?.value ?? 0) }));
      return { payload: heatmapDataUpdated, details: detailsData || {} };
    }
  },
  {
    key: 'engagement_distribution',
    graph: 'StackedHistogram',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'engagement_distribution');
      const engagementData = metric?.data;
      const detailsData = {
        tooltipDescription: 'This chart shows how participant engagement levels are distributed across all calls.',
        points: [
          'Engagement Score measures participant involvement during calls',
          'Higher scores indicate more active participation'
        ],
        bottomDescription: 'Distribution stats',
        subPoints: [
          `Mean: ${engagementData?.mean || 0} | Median: ${engagementData?.median || 0} | Std Dev: ${engagementData?.std_dev || 0}`,
          `Above Target: ${engagementData?.threshold || 0}% of calls | Target: ${engagementData?.target || ''}`,
          `Below Threshold: ${engagementData?.below_threshold_count || 0} of calls | Below Threshold: ${engagementData?.below_threshold_pct || 0}`,
        ]
      };
      const buckets = (engagementData?.buckets || []).map((bucket: any) => ({ range: bucket.range_label, count: bucket.count, percentage: bucket.percentage }));
      return { payload: buckets, details: detailsData || {} };
    }
  },
  {
    key: 'speech_behavior',
    graph: 'CompareInfoCards',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'speech_behavior');
      const comData = metric?.data;
      const payload = [];
      const detailsData = {
        tooltipDescription: 'Detailed speech behavior metrics for participants.',
        points: [
          'Talk Balance indicates the proportion of time a participant speaks during calls',
          'Interruption Rate measures how often a participant interrupts others',
          'Avg Pause Duration reflects the average length of pauses taken by a participant'
        ],
      };

      if (comData?.talk_balance) {
        const tb = comData.talk_balance;
        payload.push({
          cardTitle: tb.metric_name || "Talk Balance",
          score: `${tb.value || 0}${tb.unit || '%'}`,
          lable: tb.status === 'good' ? 'Good' : tb.status === 'warning' ? 'Warning' : 'Alert',
          targetLine: `Benchmark: ${tb.benchmark}${tb.unit || '%'}`,
          targetSubLine: tb.status === 'good' ? 'Within Range' : tb.status === 'warning' ? 'Above Target' : 'Below Target',
          description: `Trend: ${tb.trend || 'stable'}`
        });
      }

      if (comData?.interruption_rate) {
        const ir = comData.interruption_rate;
        payload.push({
          cardTitle: ir.metric_name || "Interruption Rate",
          score: `${ir.value || 0}${ir.unit || '/min'}`,
          lable: ir.status === 'good' ? 'Good' : ir.status === 'warning' ? 'Warning' : 'Alert',
          targetLine: `Benchmark: ${ir.benchmark}${ir.unit || '/min'}`,
          targetSubLine: ir.status === 'good' ? 'Within Range' : ir.status === 'warning' ? 'Above Target' : 'Below Target',
          description: `Trend: ${ir.trend || 'stable'}`
        });
      }

      if (comData?.avg_pause_duration) {
        const apd = comData.avg_pause_duration;
        payload.push({
          cardTitle: apd.metric_name || "Avg Pause Duration",
          score: `${apd.value || 0}${apd.unit || 'sec'}`,
          lable: apd.status === 'good' ? 'Good' : apd.status === 'warning' ? 'Warning' : 'Alert',
          targetLine: `Benchmark: ${apd.benchmark}${apd.unit || 'sec'}`,
          targetSubLine: apd.status === 'good' ? 'Within Range' : apd.status === 'warning' ? 'Above Target' : 'Below Target',
          description: `Trend: ${apd.trend || 'stable'}`
        });
      }

      return { payload, details: detailsData || {} };
    }
  },
  {
    key: 'duration_quality',
    graph: 'ScatterChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'duration_quality');
      const scatterData = metric?.data;
      scatterData.tooltipDescription = `${scatterData?.summary_description} ${scatterData?.tooltip_info?.what_it_measures || ''}`;
      scatterData.tooltipPoints = scatterData?.tooltip_info?.suggested_actions || [];
      scatterData.bottomDescription = 'Duration vs Quality distribution';
      scatterData.subPoints = [
        'Each point represents a call',
        'Quadrants indicate performance categories'
      ];
      const points = (scatterData?.points || []).map((point: any) => ({
        call_id: point?.call_id || '',
        duration: point?.duration_minutes || 0,
        quality: point?.quality_score || 0,
        zone: point?.quadrant?.toLowerCase() || 'unknown',
        tooltip_text: point?.tooltip_text || '',
      }));
      return { payload: points, details: scatterData || {} };
    }
  },
  {
    key: 'period_comparison',
    graph: 'CompareInfoCards',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'period_comparison');
      const comparisonData = metric?.data;
      const detailsData = {
        tooltipDescription: comparisonData?.summary_description || 'Comparison of metrics over different periods.',
        points: comparisonData?.tooltip_info?.suggested_actions || [
          'Review metrics to identify areas of improvement',
          'Focus on metrics with negative trends for coaching'
        ]
      };
      
      const payload = (comparisonData?.metrics || []).map((item: any) => {
        let label = 'Stable';
        let targetSubLine = 'No Change';
        
        if (item.trend === 'up') {
          label = item.is_improvement ? 'Improved' : 'Increased';
          targetSubLine = item.is_improvement ? 'Positive Trend' : 'Needs Attention';
        } else if (item.trend === 'down') {
          label = item.is_improvement ? 'Improved' : 'Declined';
          targetSubLine = item.is_improvement ? 'Positive Trend' : 'Needs Improvement';
        }
        
        return {
          cardTitle: item.metric_name || 'Metric',
          score: `${item.current_value}`,
          lable: label,
          targetLine: `Previous: ${item.previous_value}`,
          targetSubLine: targetSubLine,
          description: `Change: ${item.change_absolute >= 0 ? '+' : ''}${item.change_absolute}`
        };
      });
      
      return { payload, details: detailsData || {} };
    }
  },
  {
    key: 'team_performance_top',
    graph: 'PercentageLineChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'team_performance_top');
      const teamData = metric?.data;
      
      const payload = (teamData?.teams || []).map((team: any) => {
        const healthScore = team.health_score || 0;
        const percentageScore = Math.min(100, Math.max(0, healthScore));
        
        // Determine color gradient based on health score
        let colorFrom = '#ef4444'; // red for poor
        let colorTo = '#dc2626';
        
        if (healthScore >= 70) {
          colorFrom = '#22c55e'; // green for excellent
          colorTo = '#16a34a';
        } else if (healthScore >= 50) {
          colorFrom = '#f59e0b'; // orange for fair
          colorTo = '#d97706';
        }
        
        return {
          topic: team.team_name || 'Unknown Team',
          percentage: percentageScore,
          colorFrom: colorFrom,
          colorTo: colorTo,
          info: `${team.total_calls} calls | Engagement: ${team.engagement_avg}% | Positive: ${team.positive_rate}%`
        };
      });
      
      const detailsData = {
        bottomDescription: teamData?.summary_description || '',
        subPoints: [
          `Total Teams: ${teamData?.total_teams || 0}`,
          `Benchmark Score: ${teamData?.benchmark_score || 0}%`
        ],
        tooltipDescription: teamData?.tooltip_info?.what_it_measures || 'Teams ranked by interaction health score',
        points: teamData?.tooltip_info?.suggested_actions || []
      };
      
      return { payload, details: detailsData };
    }
  },
  {
    key: 'team_performance_bottom',
    graph: 'PercentageLineChart',
    transform: (data: any, fullData?: any) => {
      const metric = fullData?.metrics?.find((m: any) => m.metric_key === 'team_performance_bottom');
      const teamData = metric?.data;
      
      const payload = (teamData?.teams || []).map((team: any) => {
        const healthScore = team.health_score || 0;
        const percentageScore = Math.min(100, Math.max(0, healthScore));
        
        // Determine color gradient based on health score (emphasize warning/poor)
        let colorFrom = '#ef4444'; // red for poor
        let colorTo = '#dc2626';
        
        if (healthScore >= 70) {
          colorFrom = '#22c55e'; // green for excellent
          colorTo = '#16a34a';
        } else if (healthScore >= 50) {
          colorFrom = '#f59e0b'; // orange for fair
          colorTo = '#d97706';
        }
        
        return {
          topic: team.team_name || 'Unknown Team',
          percentage: percentageScore,
          colorFrom: colorFrom,
          colorTo: colorTo,
          info: `${team.total_calls} calls | Engagement: ${team.engagement_avg}% | Positive: ${team.positive_rate}% | ${team.needs_coaching_count || 0} need review`
        };
      });
      
      const detailsData = {
        bottomDescription: teamData?.summary_description || '',
        subPoints: [
          `Total Teams: ${teamData?.total_teams || 0}`,
          `Benchmark Score: ${teamData?.benchmark_score || 0}%`,
          'Focus coaching efforts on these teams'
        ],
        tooltipDescription: teamData?.tooltip_info?.what_it_measures || 'Teams with below-average interaction quality requiring attention',
        points: teamData?.tooltip_info?.suggested_actions || []
      };
      
      return { payload, details: detailsData };
    }
  },
];